import { createContext, useContext, useState, ReactNode, useCallback, useEffect } from "react";
import { trpc, trpcVanilla } from "@/lib/trpc";
import { authClient } from "@/lib/authClient";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";

interface User {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  role: string;
  companyId?: string | null;
  isStaff?: boolean;
  staffRole?: "SUPER_ADMIN" | "ADMIN_OPS" | "ADMIN_SUPPORT" | null;
}

interface AuthContextType {
  user: User | null;
  token: null;
  login: (email: string, password: string, captchaToken?: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  /**
   * True while we have a login hint (CSRF cookie / just logged in) but the
   * `auth.me` query has not yet resolved. Guards must show a loader instead of
   * redirecting to /login during this window (otherwise authenticated users
   * bounce on refresh / deep-link).
   */
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const LEGACY_TOKEN_KEY = "sadmin_token";
const LEGACY_USER_KEY = "sadmin_user";

/**
 * Une session existe-t-elle ?
 *
 * On lisait `constrigo_csrf`, dont le maxAge est 1 h comme le jeton d'accès :
 * passé une heure, le navigateur le supprimait, `loginHint` retombait à `false`
 * et l'app n'ESSAYAIT MÊME PAS `auth.me` — écran déconnecté alors que le
 * refresh token (30 j) était intact. `constrigo_session` vit aussi longtemps
 * que le refresh, précisément pour répondre à cette question.
 */
function hasSessionCookie(): boolean {
  return authClient.hasSession();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    localStorage.removeItem(LEGACY_USER_KEY);
  }, []);

  const [loginHint, setLoginHint] = useState<boolean>(hasSessionCookie);

  const meQuery = trpc.auth.me.useQuery(undefined, {
    enabled: loginHint,
    retry: false,
    // On ne conclut à la déconnexion que sur un refus d'authentification franc.
    // Un 500 (hoquet backend) ou un 429 (limiteur générique, partagé par IP)
    // effaçait auparavant la session côté UI alors que les cookies restaient
    // parfaitement valides.
    onError: (err) => {
      const code = err.data?.code;
      if (code === "UNAUTHORIZED" || code === "FORBIDDEN") setLoginHint(false);
    },
  });

  // Renouvellement proactif : la session staff se prolonge toute seule tant que
  // le refresh token (30 j) vit, sans le 401 visible à chaque heure pleine.
  useEffect(() => {
    return authClient.startProactiveRefresh(() => setLoginHint(false));
  }, []);

  const loginMutation = trpc.auth.login.useMutation();

  const login = useCallback(async (email: string, password: string, captchaToken?: string) => {
    try {
      // captchaToken : `undefined` en usage normal. Le serveur ne le réclame
      // qu'au-delà d'un seuil d'échecs récents (CAPTCHA adaptatif), et le
      // signale alors via la sentinelle CAPTCHA_REQUIRED.
      const result = await loginMutation.mutateAsync({ email, password, captchaToken });

      // `in` : seules les réponses staff portent isStaff/staffRole dans l'union
      // renvoyée par auth.login (un freelance de prospection n'entre jamais ici).
      const loggedUser = result.user;
      if (!("isStaff" in loggedUser) || !loggedUser.isStaff || !("staffRole" in loggedUser) || loggedUser.staffRole !== "SUPER_ADMIN") {
        throw new Error("Accès refusé. Seul le Super Admin peut accéder à la tour de contrôle.");
      }

      setLoginHint(true);
      void meQuery.refetch();
    } catch (error: any) {
      if (error?.message?.includes("Failed to fetch")) {
        throw new Error("Le serveur backend n'est pas accessible. Assurez-vous qu'il est démarré sur http://localhost:3001");
      }
      throw error;
    }
  }, [loginMutation, meQuery]);

  const logout = useCallback(async () => {
    // Purge l'horodatage « refresh récent » et prévient les autres onglets.
    authClient.markLoggedOut();
    try {
      await trpcVanilla.auth.logoutSelf.mutate(undefined as any);
    } catch {
      // Cookie might already be stale — ignore.
    }
    setLoginHint(false);
    if (typeof window !== "undefined") {
      localStorage.removeItem(LEGACY_TOKEN_KEY);
      localStorage.removeItem(LEGACY_USER_KEY);
    }
  }, []);

  const user = (loginHint ? (meQuery.data as User | undefined) : null) ?? null;

  // Keep Sentry aware of WHO is connected so every event/log names the user.
  useEffect(() => {
    if (user) {
      setSentryUser({
        id: user.id,
        email: user.email,
        role: user.staffRole ?? user.role,
        companyId: user.companyId ?? undefined,
      });
    } else {
      clearSentryUser();
    }
  }, [user]);

  // We have a reason to believe the user is authenticated (CSRF cookie at boot,
  // or a fresh login) but `auth.me` has not resolved yet. Guards must wait
  // instead of redirecting to /login. `onError` flips `loginHint` to false, so
  // a confirmed-absent auth ends the loading window.
  const isLoading = loginHint && !user;

  return (
    <AuthContext.Provider
      value={{
        user,
        token: null,
        login,
        logout,
        isAuthenticated: loginHint && !!user,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
